import { useQuery } from "@tanstack/react-query";
import { DateRange } from "react-day-picker";

import { guacHttpClient } from "@/lib/http";
import {
  DateFilterOption,
  HttpResponse,
  LeaderboardDetailResponse,
  LeaderboardMetric,
} from "@/types";

const prepareDateRangeQueryParams = (
  queryParams: URLSearchParams,
  dateRange?: DateRange,
) => {
  if (dateRange?.from) {
    queryParams.append("startDate", String(dateRange.from));
  }
  if (dateRange?.to) {
    // End date must be set to the following day to include the entire day.
    const endDate = new Date(dateRange.to);
    endDate.setDate(endDate.getDate() + 1);
    queryParams.append("endDate", String(endDate));
  }
};

export function useGetLeaderboardDetail({
  userId,
  metric,
  timePeriod,
  dateRange,
}: {
  userId: number | null;
  metric: LeaderboardMetric | null;
  timePeriod: DateFilterOption;
  dateRange?: DateRange;
}) {
  const canFetch = metric !== null;

  return useQuery({
    queryKey: [
      "leaderboard-detail",
      userId,
      metric,
      timePeriod,
      dateRange?.from,
      dateRange?.to,
    ],
    queryFn: async () => {
      if (metric === null) {
        throw new Error("Missing metric");
      }

      const queryParams = new URLSearchParams({ metric });
      if (userId !== null) queryParams.set("userId", String(userId));

      if (timePeriod === DateFilterOption.Custom) {
        prepareDateRangeQueryParams(queryParams, dateRange);
      } else {
        queryParams.set("period", timePeriod);
      }

      const path = `staffer-metrics/detail?${queryParams.toString()}`;
      const res = await guacHttpClient.get<
        LeaderboardDetailResponse,
        HttpResponse<LeaderboardDetailResponse>
      >(path);

      return res.data;
    },
    enabled: canFetch,
  });
}
